import React, { useMemo, useRef, useState } from 'react';
import { Box, Button, useMediaQuery } from '@mui/material';

import BlockHeader from '../../common/BlockHeader';
import BlockSubHeader from '../../common/BlockSubHeader';
import ContentContainer from '../../common/ContentContainer';
import { PurrformanceChart } from './components/PurrformanceChart';

import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';

import { ReactComponent as Arrow } from '../../../assets/common/arrow_left.svg';
import isle48 from '../../../assets/common/icons/isle48.svg';
import walken48 from '../../../assets/common/icons/walken48.svg';
import cup48 from '../../../assets/common/icons/cup48.svg';

import { useDataState } from '../../../providers/DataProvider.hooks';

export enum PurrformanceChartType {
    CLASH_WINS = 'Clash_wins',
    HIGHEST_WIN_RATE = 'Highest_win_rate',
    WLKN_GAINED = 'WLKN_Gained',
}

export const PurrformanceCharts = () => {
    const { clans } = useDataState();
    const carouselRef = useRef<Carousel>(null);
    const [nextSlide, setNextSlide] = useState(0);
    const isMobile = useMediaQuery('(max-width:1200px)');

    const carouselResponsive = {
        superLargeDesktop: {
            // the naming can be any, depends on you.
            breakpoint: { max: 4000, min: 3000 },
            items: 2,
        },
        desktop: {
            breakpoint: { max: 3000, min: 1024 },
            items: 2,
        },
        tablet: {
            breakpoint: { max: 1024, min: 464 },
            items: 2,
        },
        mobile: {
            breakpoint: { max: 464, min: 0 },
            items: 2,
        },
    };

    const tablesData = useMemo(
        () => ({
            win: clans?.data
                ?.sort((a, b) => a.rank_win - b.rank_win)
                .map((clan) => ({
                    name: clan.clan,
                    picture: clan.picture,
                    score: clan.win,
                    position: clan.rank_win,
                    clan: clan,
                })),
            winrate: clans?.data
                ?.sort((a, b) => a.rank_winrate - b.rank_winrate)
                .map((clan) => ({
                    name: clan.clan,
                    picture: clan.picture,
                    score: clan.winrate.toFixed(2) + '%',
                    position: clan.rank_winrate,
                    clan: clan,
                })),
            wlkn: clans?.data
                ?.sort((a, b) => a.rank_wlkn - b.rank_wlkn)
                .map((clan) => ({
                    name: clan.clan,
                    picture: clan.picture,
                    score: clan.wlkn.toLocaleString('en-US', { minimumFractionDigits: 2 }),
                    position: clan.rank_wlkn,
                    clan: clan,
                })),
        }),
        [clans?.data],
    );

    return (
        <ContentContainer withoutMargin>
            <Box
                display={'flex'}
                justifyContent={'space-between'}
                sx={{
                    padding: '0 20px',
                }}
            >
                <Box>
                    <BlockHeader text="Purr-formance Chart"></BlockHeader>
                    <BlockSubHeader
                        text="The last update of the leaderboards data was"
                        time={clans?.last_modified}
                    ></BlockSubHeader>
                </Box>
                <Box>
                    <Button
                        disabled={nextSlide === 0}
                        sx={{
                            border: '1px solid #D8DFE5',
                            borderRadius: '50%',
                            width: '64px',
                            height: '64px',
                            color: '#0F0E0E',
                            '&:hover': {
                                border: '1px solid #C6CFD8',
                                background: 'transparent',
                            },
                            '&:disabled': {
                                '& path': {
                                    stroke: '#D8DFE5',
                                },
                            },
                        }}
                        onClick={() => {
                            if (carouselRef && carouselRef.current) {
                                carouselRef.current.goToSlide(nextSlide - 1);
                            }
                        }}
                    >
                        <Arrow></Arrow>
                    </Button>
                    <Button
                        disabled={nextSlide === 1}
                        sx={{
                            border: '1px solid #D8DFE5',
                            borderRadius: '50%',
                            width: '64px',
                            height: '64px',
                            color: '#0F0E0E',
                            transform: 'rotate(180deg)',
                            marginLeft: '10px',
                            '&:hover': {
                                border: '1px solid #C6CFD8',
                                background: 'transparent',
                            },
                            '&:disabled': {
                                '& path': {
                                    stroke: '#D8DFE5',
                                },
                            },
                        }}
                        onClick={() => {
                            if (carouselRef && carouselRef.current) {
                                carouselRef.current.goToSlide(nextSlide + 1);
                            }
                        }}
                    >
                        <Arrow></Arrow>
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    position: 'relative',
                    maxWidth: '100%',
                    marginBottom: '60px',
                    padding: '0 20px 0 0',

                    '& .carousel': {
                        overflow: 'visible',
                        '& li': {
                            transition: 'all ease 0.5s',
                        },
                        '& li[aria-hidden="true"]': {
                            opacity: isMobile ? 1 : 0.2,
                        },
                    },
                }}
            >
                <Carousel
                    className="carousel"
                    ref={carouselRef}
                    arrows={false}
                    beforeChange={(nextSlide) => {
                        setNextSlide(nextSlide);
                    }}
                    responsive={carouselResponsive}
                >
                    <PurrformanceChart
                        type={PurrformanceChartType.CLASH_WINS}
                        data={tablesData.win}
                        image={<img src={isle48} alt="wins"></img>}
                        title="Clash wins"
                    ></PurrformanceChart>
                    <PurrformanceChart
                        type={PurrformanceChartType.HIGHEST_WIN_RATE}
                        data={tablesData.winrate}
                        image={<img src={cup48} alt="winrate"></img>}
                        title="Highest win rate"
                    ></PurrformanceChart>
                    <PurrformanceChart
                        type={PurrformanceChartType.WLKN_GAINED}
                        data={tablesData.wlkn}
                        image={<img src={walken48} alt="earnings"></img>}
                        title="WLKN Gained"
                    ></PurrformanceChart>
                </Carousel>
            </Box>
        </ContentContainer>
    );
};
